/**
 * CancelJobModal — dispatcher job cancellation confirmation (ClickUp 86bamr1cz / DEC-122).
 *
 * PATCH /v1/jobs/:id/cancel. Two flows keyed on the job's status:
 *
 *   PENDING (DISPATCH_MANAGER+): single-step modal — a required reason (min 5
 *     chars) + [Cancel Job] (red) / [Keep Job]. Sends { reason }.
 *
 *   EN_ROUTE_TO_PICKUP (DISPATCH_MANAGER+, widened from TENANT_ADMIN by DEC-123
 *     point 3): two-step modal —
 *     Step 1: amber warning "the driver has already accepted this job and may be
 *             en route" + [Continue] / [Go Back].
 *     Step 2: required reason + [Confirm Cancel] (red) / [Go Back].
 *     Sends { reason, confirmDriverEnRoute: true }.
 *
 * The server is the authoritative gate; this modal is only shown for cancellable
 * statuses to roles that pass the client UX gate (see JobDetailModal). On success
 * it toasts "Job cancelled", invalidates the job queries (the job drops off the
 * board — CANCELLED is terminal), and calls onCancelled so the parent closes.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiOpts, useApiMutation } from '@lodex-pro/web-shared';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { JOB_DETAIL_QUERY_KEY } from './use-job-detail';

const REASON_MIN = 5;

interface CancelBody {
  reason: string;
  confirmDriverEnRoute?: boolean;
}

export interface CancelJobModalProps {
  readonly jobId: string | null;
  readonly haulId: string | null;
  /** Job status — decides the single-step (PENDING) vs two-step (EN_ROUTE) flow. */
  readonly statusCode: string | null;
  readonly open: boolean;
  readonly onClose: () => void;
  /** Called after a successful cancellation so the parent can close the detail panel. */
  readonly onCancelled: () => void;
}

const textareaStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '80px',
  padding: '8px 10px',
  fontSize: '14px',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  color: 'var(--color-text-primary)',
  resize: 'vertical',
};

const labelStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  color: 'var(--color-text-tertiary)',
  display: 'block',
  marginBottom: '4px',
};

export function CancelJobModal({
  jobId,
  haulId,
  statusCode,
  open,
  onClose,
  onCancelled,
}: CancelJobModalProps): React.JSX.Element {
  const apiOpts = useApiOpts();
  const queryClient = useQueryClient();
  const isEnRoute = statusCode === 'EN_ROUTE_TO_PICKUP';

  // Two-step flow for EN_ROUTE starts on the warning step; PENDING goes straight
  // to the reason step. Reset whenever the modal (re)opens.
  const [step, setStep] = React.useState<'warn' | 'reason'>('reason');
  const [reason, setReason] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setStep(isEnRoute ? 'warn' : 'reason');
      setReason('');
      setError(null);
    }
  }, [open, isEnRoute]);

  const mutation = useApiMutation<CancelBody, { ok: boolean; status: string }>(
    apiOpts,
    `/v1/jobs/${jobId ?? ''}/cancel`,
    'PATCH',
    {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ['jobs'] });
        if (jobId) void queryClient.invalidateQueries({ queryKey: JOB_DETAIL_QUERY_KEY(jobId) });
        toast.success('Job cancelled');
        onCancelled();
      },
      onError: () => {
        setError('Could not cancel the job. Try again.');
      },
    },
  );

  const reasonValid = reason.trim().length >= REASON_MIN;

  const handleConfirm = React.useCallback((): void => {
    if (!reasonValid || jobId === null) return;
    setError(null);
    const body: CancelBody = isEnRoute
      ? { reason: reason.trim(), confirmDriverEnRoute: true }
      : { reason: reason.trim() };
    mutation.mutate(body);
  }, [reasonValid, jobId, isEnRoute, reason, mutation]);

  const title = `Cancel job ${haulId ?? ''}`.trim();

  return (
    <Dialog
      open={open}
      onOpenChange={(next): void => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="w-[calc(100vw-2rem)] max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {isEnRoute && step === 'warn' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div
              role="alert"
              style={{
                display: 'flex',
                gap: '8px',
                padding: '12px',
                borderRadius: '12px',
                border: '1px solid var(--color-warning)',
                backgroundColor: 'rgba(246, 182, 94, 0.12)',
                color: 'var(--color-warning)',
                fontSize: '14px',
                lineHeight: 1.4,
              }}
            >
              <span aria-hidden="true">⚠</span>
              <span>The driver has already accepted this job and may be en route.</span>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label htmlFor="cancel-reason" style={labelStyle}>
              Reason for cancellation
            </label>
            <textarea
              id="cancel-reason"
              value={reason}
              onChange={(e): void => setReason(e.target.value)}
              placeholder="Why is this job being cancelled? (min 5 characters)"
              style={textareaStyle}
              aria-label="Cancellation reason"
            />
            {reason.trim().length > 0 && !reasonValid && (
              <span style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
                Reason must be at least {REASON_MIN} characters.
              </span>
            )}
          </div>
        )}

        {error !== null && (
          <p role="alert" style={{ fontSize: '13px', color: 'var(--color-danger)', margin: 0 }}>
            {error}
          </p>
        )}

        <DialogFooter>
          {isEnRoute && step === 'warn' ? (
            <>
              <Button type="button" variant="outline" onClick={onClose}>
                Go Back
              </Button>
              <Button
                type="button"
                onClick={(): void => setStep('reason')}
                style={{
                  backgroundColor: 'var(--color-warning)',
                  color: 'var(--color-on-status)',
                  borderColor: 'var(--color-warning)',
                }}
              >
                Continue
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={isEnRoute ? (): void => setStep('warn') : onClose}
              >
                {isEnRoute ? 'Go Back' : 'Keep Job'}
              </Button>
              <Button
                type="button"
                disabled={!reasonValid || mutation.isPending}
                onClick={handleConfirm}
                style={{
                  backgroundColor: 'var(--color-danger)',
                  color: 'var(--color-on-accent)',
                  borderColor: 'var(--color-danger)',
                  opacity: !reasonValid || mutation.isPending ? 0.6 : 1,
                }}
              >
                {mutation.isPending ? 'Cancelling…' : isEnRoute ? 'Confirm Cancel' : 'Cancel Job'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
